import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import api from '../../api';

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Parse datetime string as UTC regardless of browser timezone
  const parseUTC = (iso) => {
    if (!iso) return new Date();
    if (!iso.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(iso)) {
      return new Date(iso.replace(' ', 'T') + 'Z');
    }
    return new Date(iso);
  };

  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showWorksModal, setShowWorksModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientWorks, setClientWorks] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [showCarousel, setShowCarousel] = useState(false);
  const [carouselPhotos, setCarouselPhotos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: '', age: '', phone: '', address: '', birthday: '' });

  useEffect(() => { fetchClients(); }, []);
  useEffect(() => {
    if (!search.trim()) setFiltered(clients);
    else setFiltered(clients.filter(c => c.full_name.toLowerCase().includes(search.toLowerCase()) || (c.phone && c.phone.includes(search))));
  }, [search, clients]);

  const fetchClients = async () => {
    try { const r = await api.get(`/clients/${user?.id}`); setClients(r.data); }
    catch (e) { console.error(e); }
  };

  const openModal = (client) => {
    if (client) {
      setEditing(client);
      setForm({ full_name: client.full_name, age: client.age?.toString() || '', phone: client.phone || '', address: client.address || '', birthday: client.birthday || '' });
    } else {
      setEditing(null);
      setForm({ full_name: '', age: '', phone: '', address: '', birthday: '' });
    }
    setShowModal(true);
  };

  const viewClientWorks = async (client) => {
    setSelectedClient(client);
    try {
      const r = await api.get(`/custom-work/client/${client.id}`);
      setClientWorks(r.data);
      setShowWorksModal(true);
    } catch (e) { console.error(e); setClientWorks([]); setShowWorksModal(true); }
  };

  const openCarousel = (photos, index = 0) => {
    setCarouselPhotos(photos);
    setPhotoIndex(index);
    setShowCarousel(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.full_name) { alert('El nombre es obligatorio'); return; }
    setLoading(true);
    try {
      const data = { business_user_id: user?.id, full_name: form.full_name, age: form.age ? parseInt(form.age) : null, phone: form.phone || null, address: form.address || null, birthday: form.birthday || null };
      if (editing) await api.put(`/clients/${editing.id}`, data);
      else await api.post('/clients', data);
      setShowModal(false); fetchClients();
    } catch (err) { alert(err.response?.data?.detail || 'Error al guardar'); }
    finally { setLoading(false); }
  };

  const del = (client) => {
    if (!confirm(`¿Eliminar a "${client.full_name}"?`)) return;
    api.delete(`/clients/${client.id}`).then(fetchClients).catch(() => alert('Error al eliminar'));
  };

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title" style={{ flex: 1 }}>Clientes</h1>
        <button className="add-btn" onClick={() => openModal()}>+</button>
      </div>

      <div className="search-container">
        <div className="search-input-wrap">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Buscar cliente por nombre o teléfono..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="content-scroll">
        {filtered.map(c => (
          <div key={c.id} className="card-row" onClick={() => viewClientWorks(c)} style={{ cursor: 'pointer' }}>
            <div className="card-row-info">
              <div className="card-row-title">{c.full_name}</div>
              {c.age && <div className="card-row-detail">Edad: {c.age} años</div>}
              {c.phone && <div className="card-row-detail">Tel: {c.phone}</div>}
              {c.birthday && <div className="birthday-tag">🎁 {c.birthday}</div>}
            </div>
            <div className="card-row-actions" onClick={e => e.stopPropagation()}>
              <button className="icon-btn-sm" onClick={() => openModal(c)}>✏️</button>
              <button className="icon-btn-sm danger" onClick={() => del(c)}>🗑️</button>
            </div>
          </div>
        ))}
        {clients.length === 0 && <div className="empty-state"><span className="empty-icon">👥</span><p className="empty-text">No hay clientes registrados</p></div>}
      </div>

      {/* Edit/Create Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">{editing ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
            <form onSubmit={save}>
              <div className="form-group"><label className="label">Nombre y Apellido *</label><input className="input" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required /></div>
              <div className="form-group"><label className="label">Edad</label><input className="input" type="number" value={form.age} onChange={e => setForm({...form, age: e.target.value})} /></div>
              <div className="form-group"><label className="label">Teléfono</label><input className="input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              <div className="form-group"><label className="label">Dirección</label><input className="input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div className="form-group"><label className="label">Cumpleaños (DD/MM)</label><input className="input" value={form.birthday} onChange={e => setForm({...form, birthday: e.target.value})} placeholder="01/01" /></div>
              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Works History Modal */}
      {showWorksModal && (
        <div className="modal-overlay" onClick={() => setShowWorksModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh' }}>
            <h3 className="modal-title">📋 Historial de {selectedClient?.full_name}</h3>
            
            {clientWorks.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px 20px' }}>
                <span className="empty-icon">📝</span>
                <p className="empty-text">Sin trabajos registrados</p>
              </div>
            ) : (
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {clientWorks.map(work => (
                  <div key={work.id} className="work-history-card">
                    <div className="work-header">
                      <span className="work-date">{parseUTC(work.created_at).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</span>
                      <span className="work-price">${work.price}</span>
                    </div>
                    {work.description && <p className="work-description">{work.description}</p>}
                    <div className="work-details">
                      <span>💰 Ganancia: ${work.profit}</span>
                      {work.stock_names?.length > 0 && <span>📦 {work.stock_names.join(', ')}</span>}
                      <span>{work.payment_method === 'efectivo' ? '💵' : '📱'} {work.payment_method}</span>
                    </div>
                    {work.photos?.length > 0 && (
                      <div className="work-photos">
                        {work.photos.map((photo, i) => (
                          <img key={i} src={photo} alt="" onClick={() => openCarousel(work.photos, i)} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="modal-buttons" style={{ marginTop: '16px' }}>
              <button className="btn-primary" style={{ width: '100%' }} onClick={() => setShowWorksModal(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Carousel */}
      {showCarousel && (
        <div className="carousel-overlay" onClick={() => setShowCarousel(false)}>
          <button className="carousel-close" onClick={() => setShowCarousel(false)}>×</button>
          <button className="carousel-nav prev" onClick={e => { e.stopPropagation(); setPhotoIndex(i => i > 0 ? i - 1 : carouselPhotos.length - 1); }}>‹</button>
          <img src={carouselPhotos[photoIndex]} alt="" className="carousel-image" onClick={e => e.stopPropagation()} />
          <button className="carousel-nav next" onClick={e => { e.stopPropagation(); setPhotoIndex(i => i < carouselPhotos.length - 1 ? i + 1 : 0); }}>›</button>
          <div className="carousel-counter">{photoIndex + 1} / {carouselPhotos.length}</div>
        </div>
      )}
    </div>
  );
}
